import { http } from 'utils'
import { set, findIndex } from 'lodash'

// Endorsements mutation imports.
import { SET_ENDORSEMENTS, SET_ENDORSEMENT_PROPERTY, ADD_ENDORSEMENT, REMOVE_ENDORSEMENT } from 'vx/mutations'

// Endorsements state.
const initialEndorsementState = () => ({
  id: null,
  user_id: null,
  company_name: null,
  description: null,
  date_endorsed: null
})

const state = {
  endorsements: []
}

// Endorsements state getters.
const getters = {
  /**
   * Determine if we do not have any endorsements.
   *
   * @return bool
   */
  haveNoEndorsements: state => state.endorsements.length === 0,

  /**
   * Determine if we have an unsaved endorsement.
   *
   * @return bool
   */
  haveUnsavedEndorsement: state => findIndex(state.endorsements, endorsement => endorsement.id === null) > -1
}

// Endorsements actions.
const actions = {
  /**
   * Get all endorsements for a talent.
   *
   * @return Promise
   */
  getTalentEndorsements ({ commit }, { talentId }) {
    return http.get(`users/${talentId}/talent_endorsements`)
      .then((response) => {
        commit(SET_ENDORSEMENTS, response.data)

        return response
      })
  },

  /**
   * Update an existing endorsement.
   *
   * @return Promise
   */
  updateTalentEndorsement (state, { id, talentId, payload }) {
    return http.put(`users/${talentId}/talent_endorsements/${id}`, payload)
  },

  /**
   * Store a new endorsement against a talent.
   *
   * @return Promise
   */
  storeTalentEndorsement (state, { talentId, payload }) {
    return http.post(`users/${talentId}/talent_endorsements`, payload)
  },

  /**
   * Store a new endorsement against a talent.
   *
   * @return Promise
   */
  deleteTalentEndorsement ({ commit }, { id, talentId }) {
    return http.delete(`users/${talentId}/talent_endorsements/${id}`)
      .then((response) => {
        commit(REMOVE_ENDORSEMENT, { id })

        return response
      })
  },

  addEndorsement ({ commit }) {
    commit(ADD_ENDORSEMENT)
  },

  setEndorsementProperty ({ commit }, { index, property, value }) {
    commit(SET_ENDORSEMENT_PROPERTY, { index, property, value })
  },

  removeEndorsement ({ commit }, { id, index }) {
    commit(REMOVE_ENDORSEMENT, { id, index })
  }
}

// Endorsements state mutations.
const mutations = {
  /**
   * Set all endorsements on the state.
   *
   * @return void
   */
  [SET_ENDORSEMENTS] (state, endorsements) {
    state.endorsements = endorsements
  },

  /**
   * Set a property on a given endorsement within the state.
   *
   * @return void
   */
  [SET_ENDORSEMENT_PROPERTY] (state, { index, property, value }) {
    set(state.endorsements[index], property, value)
  },

  /**
   * Add a new initial endorsement to the state.
   *
   * @return void
   */
  [ADD_ENDORSEMENT] (state) {
    state.endorsements.push(initialEndorsementState())
  },

  /**
   * Remove an endorsement from state by either the ID or its index.
   *
   * If removing by ID we'll attempt to find the index of the endorsement within the state.
   *
   * @return void
   */
  [REMOVE_ENDORSEMENT] (state, { id, index }) {
    if (id !== undefined) {
      index = findIndex(state.endorsements, endorsement => endorsement.id === id)
    }

    if (index !== -1) {
      state.endorsements.splice(index, 1)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
